import React, { useState } from "react";
import styled from "styled-components";
import { FaStar, FaVideo, FaComment, FaPhone } from "react-icons/fa";
import { IoCall } from "react-icons/io5";
import { useDispatch } from "react-redux";
import RateSessionModal from "./Modals/RateSessionModal";
import { rateAndReviewSession } from "../../redux/actions/mindfulness";

// Styled Components
const Card = styled.div`
  border-radius: 12px;
  border: 1px solid #e0e0e0;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  max-width: 100%;
  margin: 0 auto;
  font-family: Montserrat;
  font-weight: 400;
  margin-bottom: 1rem;
`;

const TopSection = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
`;

const ProfileImage = styled.img`
  width: 6.25rem;
  height: 6.25rem;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #f0f0f0;
  flex-shrink: 0;
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Title = styled.h2`
  font-size: 1.25rem;
  font-weight: 500;
  color: #2d3748;
  margin: 0;
`;

const Status = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #22c55e;
  font-size: 0.9rem;
`;

const StatusDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #22c55e;
`;

const Price = styled.div`
  font-size: 1.3rem;
  font-weight: 400;
  color: #e53e3e;
  align-self: flex-start;
  margin-top: 4px;
`;

const CreatorInfo = styled.div`
  font-size: 1rem;
  color: #4a5565;
  text-transform: capitalize;
  font-weight: 500;
`;

const Tag = styled.span`
  background: #fef2f2;
  color: #e7000b;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 400;
  align-self: flex-start;
  /* margin-right: .75rem ; */
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const CallButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: #e6f7ff;
  border: none;
  border-radius: 20px;
  padding: 8px 16px;
  font-size: 0.9rem;
  color: #1890ff;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #edf2f7;
    border-color: #94a3b8;
  }

  svg {
    font-size: 1.1rem;
  }
`;

const RatingSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  font-weight: 400;
  color: #4a5565;
  margin-left: 1.25rem;
`;

const Description = styled.p`
  font-size: .875rem;
  font-weight: 400;
  color: #4a5568;
  line-height: 1.5;
  margin: 0;
`;

const InfoRow = styled.div`
  display: flex;
  gap: 24px;
  /* margin-top: 0.75rem; */
  color: #718096;
  font-size: 0.875rem;
  font-weight: 400;
`;

const InfoItem = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  /* margin-top: 20px; */
  flex-wrap: wrap;
`;

const PrimaryButton = styled.button`
  background: #fb2c36;
  color: white;
  border: none;
  border-radius: 10px;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  flex: 1;
  min-width: 200px;
  transition: background 0.2s;
  width: 80%;

  &:hover {
    background: #c53030;
  }
`;

const BookAgainBtn = styled.button`
  background: #FEF2F2;
  color: #E7000B;
  border: none;
  border-radius: 10px;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  flex: 1;
  min-width: 200px;
  width: 100%;
  transition: background 0.2s;

  /* &:hover {
    background: #c53030;
  } */
`;

const SecondaryButton = styled.button`
  background: white;
  color: #4a5568;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  flex: 1;
  min-width: 200px;
  transition: all 0.2s;
  width: 20%;

  &:hover {
    background: #f8fafc;
    border-color: #94a3b8;
  }
`;

const ReviewButton = styled.button`
  background: #f0f4ff;
  color: #4a6cf7;
  border: 1px solid #c7d2fe;
  border-radius: 10px;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  flex: 1;
  min-width: 200px;
  width: 100%;
  transition: background 0.2s;

  &:hover {
    background: #e0e7ff;
  }
`;

// Main Component
const BookingCard = ({ booking }) => {
  const dispatch = useDispatch();
  const [showRateModal, setShowRateModal] = useState(false);
  // Format date and time
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Determine call type icon
  const getCallTypeIcon = (type) => {
    switch (type) {
      case 'video_call':
        return <FaVideo />;
      case 'chat':
        return <FaComment />;
      case 'call':
        return <IoCall />;
      default:
        return <IoCall />;
    }
  };

  const getCallTypeLabel = (type) => {
    switch (type) {
      case 'video_call':
        return 'Video Call';
      case 'chat':
        return 'Chat';
      case 'call':
        return 'Voice Call';
      default:
        return 'Call';
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return '#22c55e';
      case 'pending':
        return '#f59e0b';
      case 'cancelled':
        return '#ef4444';
      case 'confirmed':
        return '#3b82f6';
      default:
        return '#6b7280';
    }
  };

  return (
    <Card>
      <TopSection>
        <ProfileImage
          src={
            booking.creator_profile_pic?.trim() ||
            "https://via.placeholder.com/80"
          }
          alt={booking.creator_name}
        />
        <ContentWrapper>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <TitleSection>
              <Title>{booking.session_name}</Title>
              <CreatorInfo>with {booking.creator_name}</CreatorInfo>
            </TitleSection>
            {/* <Price>₹{booking.amount}</Price> */}
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >
            <Tag style={{ 
              background: getStatusColor(booking.status) + '20',
              color: getStatusColor(booking.status),
              textTransform: 'capitalize'
            }}>
              {booking.status}
            </Tag>

            <CallButton>
              {getCallTypeIcon(booking.type)}
              {getCallTypeLabel(booking.type)}
            </CallButton>
          </div>

          {booking.notes && <Description>{booking.notes}</Description>}

          <InfoRow>
            <InfoItem>Start: {formatDateTime(booking.start_time)}</InfoItem>
            <InfoItem>Duration: {booking.duration} min</InfoItem>
            <InfoItem>End: {formatDateTime(booking.end_time)}</InfoItem>
          </InfoRow>
        </ContentWrapper>
      </TopSection>

      <ActionButtons>
        {booking.status === 'pending' && (
          <>
            <PrimaryButton>Join Session</PrimaryButton>
            <SecondaryButton>Cancel Booking</SecondaryButton>
          </>
        )}
        {booking.status === 'confirmed' && (
          <>
            <PrimaryButton>Join Session</PrimaryButton>
            <SecondaryButton>Reschedule</SecondaryButton>
          </>
        )}
        {booking.status === 'completed' && (
          <ReviewButton onClick={() => setShowRateModal(true)}>
            Rate &amp; Review
          </ReviewButton>
        )}
      </ActionButtons>

      {showRateModal && (
        <RateSessionModal
          booking={booking}
          onClose={() => setShowRateModal(false)}
          onSubmit={async ({ rating, feedback }) => {
            await dispatch(
              rateAndReviewSession({
                creatorSessionId: booking.creator_session,
                rating,
                review: feedback,
              })
            );
            setShowRateModal(false);
          }}
        />
      )}
    </Card>
  );
};

// Example usage with tabs for different booking categories
export default function UserBookingListing({ data = { upcoming_bookings: [], past_bookings: [], total_bookings: [] }, activeTab = 'upcoming' }) {
  console.log("Booking data inside UserBookingListing:", data);
  console.log("Active tab:", activeTab);
  
  // Determine which bookings to display based on active tab
  let bookingsToDisplay = [];
  if (activeTab === 'upcoming') {
    bookingsToDisplay = data.upcoming_bookings || [];
  } else if (activeTab === 'completed') {
    bookingsToDisplay = data.past_bookings || [];
  } else if (activeTab === 'all') {
    bookingsToDisplay = data.total_bookings || [];
  }

  console.log("Bookings to display:", bookingsToDisplay);
  console.log("Bookings count:", bookingsToDisplay.length);
  
  // Handle undefined, null, or non-array bookings
  if (!data || typeof data !== 'object') {
    console.log("Data is null, undefined, or not an object");
    return (
      <div style={{ width: "100%", boxSizing: "border-box" }}>
        <p style={{ color: "#706c6c", fontSize: "1rem", textAlign: "center", padding: "2rem" }}>
          Loading bookings...
        </p>
      </div>
    );
  }

  if (!Array.isArray(bookingsToDisplay)) {
    console.error("Bookings is not an array:", bookingsToDisplay);
    return (
      <div style={{ width: "100%", boxSizing: "border-box" }}>
        <p style={{ color: "#e53935", fontSize: "1rem", textAlign: "center", padding: "2rem" }}>
          Error: Invalid data format
        </p>
      </div>
    );
  }

  if (bookingsToDisplay.length === 0) {
    const emptyMessage = activeTab === 'upcoming' 
      ? 'No upcoming bookings found.'
      : activeTab === 'completed'
      ? 'No past bookings found.'
      : 'No bookings found.';
    
    return (
      <div style={{ width: "100%", boxSizing: "border-box" }}>
        <p style={{ color: "#706c6c", fontSize: "1rem", textAlign: "center", padding: "2rem" }}>
          {emptyMessage}
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {bookingsToDisplay.map((booking, index) => {
        console.log(`Rendering booking ${index}:`, booking);
        return (
          <BookingCard
            key={booking.id || index}
            booking={booking}
          />
        );
      })}
    </div>
  );
}
